import { feature } from 'topojson-client'
// Natural-Earth-derived country geometry (world-atlas 110m TopoJSON).
// IMPORTANT: imported (bundled) rather than fetched from /public at runtime —
// the project lives in an iCloud folder, and serving a /public file there can
// stall for seconds on first access (iCloud materialisation per request). As a
// bundled module it ships inside a normal JS chunk and decodes synchronously
// (~3 ms), so the map appears immediately the first time it is placed.
// 50m (vs the old 110m) for far more coastline detail — e.g. Norway's fjords
// read properly on the Europe map. Still bundled (not fetched) for the iCloud
// stall reason above. Maps now render as a one-time-rastered WebGL texture, so
// the heavier geometry no longer hurts interaction.
import worldTopo from './worldAtlas-50m.json'
// admin-1 (states / provinces / Bundesländer) for a small set of countries,
// pre-filtered to metropolitan extents + simplified (see scratchpad buildAdmin1).
// Bundled, not fetched, for the same iCloud-stall reason as the world geometry.
import admin1Topo from './admin1-min.json'

export interface WorldCountry {
  name: string
  // MultiPolygon: [polygon][ring][point] = [lon, lat]
  polygons: number[][][][]
}

export interface Admin1Region extends WorldCountry {
  adm0_a3: string
}

// 50m was dropped (its ~1 MB SVG was too slow to rasterise). The type is kept
// so existing content with mapResolution still type-checks; only 110m is used.
export type WorldResolution = '110m' | '50m'

function geometryToPolys(geom: TODOAny): number[][][][] {
  if (!geom) return []
  if (geom.type === 'Polygon') return [geom.coordinates]
  if (geom.type === 'MultiPolygon') return geom.coordinates
  return []
}

let cached: WorldCountry[] | null = null
function decode(): WorldCountry[] {
  const topo: TODOAny = worldTopo
  const fc: TODOAny = feature(topo, topo.objects.countries)
  return (fc.features || []).map((f: TODOAny) => ({
    name: (f.properties && f.properties.name) || '',
    polygons: geometryToPolys(f.geometry),
  }))
}

// Returns the decoded countries (decoded once, then cached). Synchronous now
// that the geometry is bundled — no fetch, no loading state.
export function getWorldFeatures(_res?: WorldResolution): WorldCountry[] {
  if (!cached) cached = decode()
  return cached
}

// The geometry is always available (bundled), so this is a stable constant —
// kept only so the renderer's infographic signature stays unchanged.
export function worldLoadToken(): string {
  return '1'
}

// ── admin-1 (states / provinces) ─────────────────────────────────────────────
let admin1Cached: Admin1Region[] | null = null
function decodeAdmin1(): Admin1Region[] {
  const topo: TODOAny = admin1Topo
  const fc: TODOAny = feature(topo, topo.objects.states)
  return (fc.features || []).map((f: TODOAny) => ({
    name: (f.properties && f.properties.name) || '',
    adm0_a3: (f.properties && f.properties.adm0_a3) || '',
    polygons: geometryToPolys(f.geometry),
  }))
}

// All bundled admin-1 regions (decoded once, then cached).
export function getAllAdmin1(): Admin1Region[] {
  if (!admin1Cached) admin1Cached = decodeAdmin1()
  return admin1Cached
}

// The states / provinces of one country, by its admin-0 A3 code (e.g. 'DEU').
export function getAdmin1Features(a3: string): Admin1Region[] {
  return getAllAdmin1().filter((r) => r.adm0_a3 === a3)
}

// A3 codes that ship with admin-1 geometry (so the UI can offer only those).
export function admin1Countries(): string[] {
  return Array.from(new Set(getAllAdmin1().map((r) => r.adm0_a3)))
}
